import React, { useState } from 'react';
import {
    Table, Button, Card, Space, Form, Input, InputNumber, Select, Radio, Typography, Tag, Modal,
    Tabs, Upload, Segmented, Drawer, Descriptions, Row, Col, Statistic, Tooltip, Dropdown, Checkbox, message
} from 'antd';
import {
    UploadOutlined, DownloadOutlined, PlusOutlined, DeleteOutlined,
    SearchOutlined, ReloadOutlined, ExportOutlined, EditOutlined, SyncOutlined, LoadingOutlined, DownOutlined
} from '@ant-design/icons';

const { Title, Text, Link } = Typography;

const ROLE_OPTIONS = [
    { label: '平台视角', value: 'platform' },
    { label: '企业视角', value: 'enterprise' },
];

// mock 当前企业（企业视角）
const CURRENT_ENT = { name: '汇云科技', id: 'ENT-HUIYUN-01' };

// 流量卡状态
const CARD_STATUS = {
    0: { text: '未识别(无效)', color: 'magenta' },
    1: { text: '可激活', color: 'default' },
    2: { text: '测试激活', color: 'blue' },
    3: { text: '测试去激活', color: 'orange' },
    4: { text: '在用', color: 'green' },
    5: { text: '停机', color: 'red' },
    6: { text: '运营商管理状态', color: 'purple' },
};

// 网络状态 / 断网类型（来源 CTIOT-5GCMP-LC001-卡状态查询）
const BLOCK_TYPE = {
    0: '未断网',
    1: '达量断网',
    2: '自主断网',
    3: '客户级机卡绑定断网',
    4: '机卡绑定断网',
    5: '区域限制断网',
    6: '首话单机卡绑定断网',
};

// ── 卡片1：流量额度 ──
const QUOTA_TYPE_OPTIONS = [
    { label: '无限流量', value: 'unlimited' },
    { label: '定义额度', value: 'custom' },
];
const QUOTA_CYCLE_OPTIONS = [
    { label: '每月重置', value: 'monthly' },
    { label: '一次定义（不重复）', value: 'cumulative' },
];
const QUOTA_CYCLE_LABEL = { monthly: '每月', cumulative: '一次定义' };

// ── 卡片2：限速配置 ──
const LIMIT_ACTION_OPTIONS = [
    { label: '不配置', value: 'none' },
    { label: '配置限速', value: 'speed' },
];
// 限速档位：CM013 自主限速 speedValue（接口提供的速率档位；不含 31 不限制）
const SPEED_TIER_OPTIONS = [
    { label: '1Kbps', value: 10 },
    { label: '64Kbps', value: 32 },
    { label: '256Kbps', value: 33 },
    { label: '512Kbps', value: 11 },
    { label: '1Mbps', value: 12 },
    { label: '3Mbps', value: 13 },
    { label: '5Mbps', value: 14 },
    { label: '7Mbps', value: 15 },
    { label: '10Mbps', value: 16 },
    { label: '20Mbps', value: 17 },
    { label: '30Mbps', value: 18 },
    { label: '40Mbps', value: 19 },
    { label: '50Mbps', value: 20 },
    { label: '60Mbps', value: 21 },
    { label: '70Mbps', value: 22 },
    { label: '80Mbps', value: 23 },
    { label: '90Mbps', value: 24 },
    { label: '100Mbps', value: 25 },
    { label: '110Mbps', value: 26 },
    { label: '120Mbps', value: 27 },
    { label: '130Mbps', value: 28 },
    { label: '140Mbps', value: 29 },
    { label: '150Mbps', value: 30 },
];
const SPEED_TIER_LABEL = SPEED_TIER_OPTIONS.reduce((m, o) => { m[o.value] = o.label; return m; }, {});

const quotaText = (r) => (r.quotaType === 'unlimited' ? '无限流量' : `${r.quotaGb} GB（${QUOTA_CYCLE_LABEL[r.quotaCycle]}）`);

const limitText = (r) => {
    if (r.limitAction === 'none') return '不配置';
    return `每月达到 ${r.limitThresholdGb} GB 限速 ${SPEED_TIER_LABEL[r.limitSpeedTier]}`;
};

// TODO: 联调时替换为接口数据
const CARD_DATA = [
    { id: 1, iccid: '8986112520808012345', msisdn: '8613800001234', imsi: '460001234567890', poolName: '麦联宝', entId: '0', entName: '鹤梦云平台', status: 4, netBlockType: 0, quotaType: 'custom', quotaGb: 30, quotaCycle: 'monthly', limitAction: 'speed', limitCycle: 'monthly', limitThresholdGb: 30, limitSpeedTier: 32, used: '4.20 GB', balance: '25.80 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-02 09:11:20', daily: [
        { date: '2026-09-13', used: '0.30 GB', balance: '25.80 GB' },
        { date: '2026-09-12', used: '0.55 GB', balance: '26.10 GB' },
        { date: '2026-09-11', used: '0.80 GB', balance: '26.65 GB' },
    ] },
    { id: 2, iccid: '8986112420209812346', msisdn: '8613800002345', imsi: '460001234567891', poolName: '麦联宝', entId: '0', entName: '鹤梦云平台', status: 1, netBlockType: 0, quotaType: 'unlimited', quotaGb: null, quotaCycle: null, limitAction: 'none', limitCycle: null, limitThresholdGb: null, limitSpeedTier: null, used: '0.00 GB', balance: '100.00 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-02 09:11:20', daily: [
        { date: '2026-09-13', used: '0.00 GB', balance: '100.00 GB' },
        { date: '2026-09-12', used: '0.00 GB', balance: '100.00 GB' },
    ] },
    { id: 3, iccid: '8986112520808012347', msisdn: '8613800003456', imsi: '460001234567892', poolName: '万物互联电信', entId: 'ENT-HUIYUN-01', entName: '汇云科技', status: 2, netBlockType: 1, quotaType: 'custom', quotaGb: 50, quotaCycle: 'monthly', limitAction: 'speed', limitCycle: 'monthly', limitThresholdGb: 50, limitSpeedTier: 12, used: '17.30 GB', balance: '32.70 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-07 15:02:41', daily: [
        { date: '2026-09-13', used: '1.10 GB', balance: '32.70 GB' },
        { date: '2026-09-12', used: '2.40 GB', balance: '33.80 GB' },
        { date: '2026-09-11', used: '1.90 GB', balance: '36.20 GB' },
    ] },
    { id: 4, iccid: '8986112420209812348', msisdn: '8613800004567', imsi: '460001234567893', poolName: '万物互联电信', entId: 'ENT-HUIYUN-01', entName: '汇云科技', status: 3, netBlockType: 2, quotaType: 'custom', quotaGb: 50, quotaCycle: 'cumulative', limitAction: 'none', limitCycle: null, limitThresholdGb: null, limitSpeedTier: null, used: '2.05 GB', balance: '47.95 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-07 15:02:41', daily: [
        { date: '2026-09-13', used: '0.45 GB', balance: '47.95 GB' },
        { date: '2026-09-12', used: '1.60 GB', balance: '48.40 GB' },
    ] },
    { id: 5, iccid: '8986112520808012349', msisdn: '8613800005678', imsi: '460001234567894', poolName: '五兴科技', entId: 'ENT-XIAOHE-88', entName: '小鹤看家', status: 5, netBlockType: 1, quotaType: 'custom', quotaGb: 20, quotaCycle: 'monthly', limitAction: 'speed', limitCycle: 'monthly', limitThresholdGb: 20, limitSpeedTier: 33, used: '18.10 GB', balance: '1.90 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-11 10:20:09', daily: [
        { date: '2026-09-13', used: '2.10 GB', balance: '1.90 GB' },
        { date: '2026-09-12', used: '3.40 GB', balance: '4.00 GB' },
    ] },
    { id: 6, iccid: '8986112420209812350', msisdn: '8613800006789', imsi: '460001234567895', poolName: '天贝云客猫', entId: 'ENT-TEST-099', entName: '产研测试验证企业', status: 6, netBlockType: 5, quotaType: 'unlimited', quotaGb: null, quotaCycle: null, limitAction: 'speed', limitCycle: 'cumulative', limitThresholdGb: 100, limitSpeedTier: 12, used: '52.60 GB', balance: '47.40 GB', lastUpdate: '2026-09-13 08:00:00', importTime: '2026-09-13 08:44:33', daily: [
        { date: '2026-09-13', used: '4.10 GB', balance: '47.40 GB' },
        { date: '2026-09-12', used: '6.20 GB', balance: '51.50 GB' },
    ] },
    { id: 7, iccid: '8986112520808012351', msisdn: '8613800007890', imsi: '460001234567896', poolName: '桔子智联', entId: 'ENT-HUIYUN-01', entName: '汇云科技', status: 0, netBlockType: null, quotaType: 'custom', quotaGb: 10, quotaCycle: 'monthly', limitAction: 'none', limitCycle: null, limitThresholdGb: null, limitSpeedTier: null, used: '0.00 GB', balance: '10.00 GB', lastUpdate: '-', importTime: '2026-09-14 09:00:00', daily: [] },
];

// 可赠送的流量套餐（任意套餐，含已下架）
const PACKAGE_OPTIONS = [
    { label: '月享10GB流量包（10 GB）', value: 'PKG_10G_M' },
    { label: '季度畅享30GB（30 GB）', value: 'PKG_30G_Q' },
    { label: '年度畅享100GB（100 GB）', value: 'PKG_100G_Y' },
    { label: '体验1GB流量包（1 GB）', value: 'PKG_1G_T' },
];
const PACKAGE_INFO = {
    PKG_10G_M: { name: '月享10GB流量包', quota: '10 GB' },
    PKG_30G_Q: { name: '季度畅享30GB', quota: '30 GB' },
    PKG_100G_Y: { name: '年度畅享100GB', quota: '100 GB' },
    PKG_1G_T: { name: '体验1GB流量包', quota: '1 GB' },
};

// TODO: 联调时替换为接口数据
const LIMIT_CHANGE_RECORDS = {
    3: [
        { id: 1, time: '2026-09-09 14:00:10', operatorName: '李运营', operatorAccount: 'liyy', before: '每月达到 50 GB 限速 256Kbps', after: '每月达到 50 GB 限速 1Mbps', reason: '客户投诉限速过低' },
    ],
};

// TODO: 联调时替换为接口数据
const GIFT_RECORDS = {
    1: [
        { id: 101, time: '2026-09-12 09:20:00', operatorName: '张运营', operatorAccount: 'zhangyy', pkgId: 'PKG_10G_M', pkgName: '月享10GB流量包', quota: '10 GB', reason: '新客礼包' },
        { id: 102, time: '2026-09-08 15:00:00', operatorName: '张运营', operatorAccount: 'zhangyy', pkgId: 'PKG_1G_T', pkgName: '体验1GB流量包', quota: '1 GB', reason: '试用补偿' },
    ],
    3: [
        { id: 301, time: '2026-09-10 10:00:00', operatorName: '李运营', operatorAccount: 'liyy', pkgId: 'PKG_30G_Q', pkgName: '季度畅享30GB', quota: '30 GB', reason: '客户补偿' },
        { id: 302, time: '2026-09-06 11:30:00', operatorName: '李运营', operatorAccount: 'liyy', pkgId: 'PKG_100G_Y', pkgName: '年度畅享100GB', quota: '100 GB', reason: '大客户加赠' },
        { id: 303, time: '2026-09-02 08:10:00', operatorName: '王运营', operatorAccount: 'wangyy', pkgId: 'PKG_10G_M', pkgName: '月享10GB流量包', quota: '10 GB', reason: '活动赠送' },
    ],
    5: [
        { id: 501, time: '2026-09-11 14:00:00', operatorName: '张运营', operatorAccount: 'zhangyy', pkgId: 'PKG_30G_Q', pkgName: '季度畅享30GB', quota: '30 GB', reason: '投诉补偿' },
    ],
};

const CardManage = () => {
    const [mockRole, setMockRole] = useState('platform');
    const [searchForm] = Form.useForm();
    const [importForm] = Form.useForm();
    const [limitForm] = Form.useForm();
    const [giftForm] = Form.useForm();
    const [giftSearchForm] = Form.useForm();
    const [importOpen, setImportOpen] = useState(false);
    const [importTab, setImportTab] = useState('manual');
    const [data, setData] = useState(CARD_DATA);
    const [editLimit, setEditLimit] = useState(null);
    const [usageCard, setUsageCard] = useState(null);
    const [giftRecords, setGiftRecords] = useState(GIFT_RECORDS);
    const [giftCard, setGiftCard] = useState(null);
    const [giftRecordCard, setGiftRecordCard] = useState(null);
    const [limitChangeRecords, setLimitChangeRecords] = useState(LIMIT_CHANGE_RECORDS);
    const [limitChangeCard, setLimitChangeCard] = useState(null);
    const [identifying, setIdentifying] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const isPlatform = mockRole === 'platform';
    const dataSource = isPlatform
        ? data
        : data.filter((d) => d.entId === CURRENT_ENT.id);

    // 导卡表单联动
    const importQuotaType = Form.useWatch('quotaType', importForm);
    const importLimitAction = Form.useWatch('limitAction', importForm);
    // 修改表单联动
    const limitFormLimitAction = Form.useWatch('limitAction', limitForm);
    // 赠送记录查询
    const giftPkgName = Form.useWatch('pkgName', giftSearchForm);
    const giftPkgId = Form.useWatch('pkgId', giftSearchForm);
    const giftType = Form.useWatch('type', giftSearchForm);

    const openImport = () => {
        importForm.resetFields();
        importForm.setFieldsValue({
            cards: [{}],
            quotaType: 'unlimited',
            limitAction: 'none',
            quotaCycle: 'monthly',
        });
        setImportTab('manual');
        setImportOpen(true);
    };

    const handleImport = () => {
        importForm.validateFields().then(() => {
            message.success('导入成功，已按流量额度与限速配置下发运营商');
            setImportOpen(false);
        });
    };

    const openEditLimit = (record) => {
        setEditLimit(record);
        limitForm.setFieldsValue({
            limitAction: record.limitAction,
            limitThresholdGb: record.limitThresholdGb,
            limitSpeedTier: record.limitSpeedTier,
            reason: '',
        });
    };

    const handleLimitSave = () => {
        limitForm.validateFields().then((values) => {
            const before = editLimit;
            setData((prev) => prev.map((d) => (
                d.id === before.id
                    ? {
                        ...d,
                        limitAction: values.limitAction,
                        limitThresholdGb: values.limitAction === 'none' ? null : values.limitThresholdGb,
                        limitSpeedTier: values.limitAction === 'speed' ? values.limitSpeedTier : null,
                    }
                    : d
            )));
            if (limitText(before) !== limitText(values)) {
                setLimitChangeRecords((prev) => ({
                    ...prev,
                    [before.id]: [
                        { id: Date.now(), time: '2026-10-14 10:00:00', operatorName: '当前用户', operatorAccount: 'current', before: limitText(before), after: limitText(values), reason: values.reason },
                        ...(prev[before.id] || []),
                    ],
                }));
            }
            message.success('保存成功');
            setEditLimit(null);
        });
    };

    const openGift = (record) => {
        setGiftCard(record);
        giftForm.resetFields();
    };

    const openGiftRecord = (record) => {
        giftSearchForm.resetFields();
        setGiftRecordCard(record);
    };

    // 识别卡号（未识别(无效)卡）：识别中 → 完成/超时后取消识别中
    const handleIdentify = (record) => {
        if (identifying.includes(record.id)) return;
        setIdentifying((prev) => [...prev, record.id]);
        setTimeout(() => {
            setIdentifying((prev) => prev.filter((id) => id !== record.id));
            message.success('识别完成，已更新卡号状态');
        }, 2500);
    };

    // 批量识别卡号（仅无效卡）
    const handleBatchIdentify = () => {
        if (selectedRowKeys.length === 0) return;
        setIdentifying((prev) => [...new Set([...prev, ...selectedRowKeys])]);
        message.success(`已提交批量识别（${selectedRowKeys.length} 张），识别中`);
        const ids = [...selectedRowKeys];
        setTimeout(() => {
            setIdentifying((prev) => prev.filter((id) => !ids.includes(id)));
            message.success('批量识别完成，已更新卡号状态');
        }, 2500);
    };

    // 批量删除（仅无效卡）
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) return;
        const ids = [...selectedRowKeys];
        Modal.confirm({
            title: `确认删除选中的 ${ids.length} 张无效卡？`,
            content: '仅「未识别(无效)」状态的卡可被删除。',
            okText: '删除',
            okButtonProps: { danger: true },
            onOk: () => {
                setData((prev) => prev.filter((d) => !ids.includes(d.id)));
                setSelectedRowKeys((prev) => prev.filter((id) => !ids.includes(id)));
                message.success('删除成功');
            },
        });
    };

    const handleGiftSave = () => {
        giftForm.validateFields().then((values) => {
            const card = giftCard;
            setGiftRecords((prev) => ({
                ...prev,
                [card.id]: [
                    { id: Date.now(), time: '2026-10-14 10:00:00', operatorName: '当前用户', operatorAccount: 'current', pkgId: values.pkgId, pkgName: PACKAGE_INFO[values.pkgId].name, quota: PACKAGE_INFO[values.pkgId].quota, reason: values.reason },
                    ...(prev[card.id] || []),
                ],
            }));
            message.success('赠送成功，已为该卡增加套餐流量');
            setGiftCard(null);
        });
    };

    const columns = [
        {
            title: 'ICCID', dataIndex: 'iccid', key: 'iccid', width: 200,
            render: (v) => <Text style={{ fontFamily: 'monospace' }} copyable>{v}</Text>,
        },
        { title: 'MSISDN', dataIndex: 'msisdn', key: 'msisdn', width: 145 },
        { title: 'IMSI', dataIndex: 'imsi', key: 'imsi', width: 165 },
        { title: '流量池', dataIndex: 'poolName', key: 'pool', width: 150 },
        ...(isPlatform
            ? [{
                title: '所属企业', key: 'ent', width: 200,
                render: (_, r) => (
                    <div>
                        <div>{r.entId === '0' ? '鹤梦云平台' : r.entName}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>ID: {r.entId}</Text>
                    </div>
                ),
            }]
            : []),
        {
            title: '卡状态', dataIndex: 'status', key: 'status', width: 190,
            render: (s, r) => {
                const tag = <Tag color={CARD_STATUS[s].color}>{CARD_STATUS[s].text}</Tag>;
                if (s !== 0) return tag;
                const isIdentifying = identifying.includes(r.id);
                return (
                    <Space size={6}>
                        {tag}
                        {isIdentifying ? (
                            <Text type="secondary" style={{ fontSize: 12 }}><LoadingOutlined /> 识别中</Text>
                        ) : (
                            <Tooltip title="识别卡号">
                                <SyncOutlined onClick={() => handleIdentify(r)} style={{ color: '#1677ff', cursor: 'pointer' }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '网络状态', dataIndex: 'netBlockType', key: 'netBlockType', width: 150,
            render: (t) => {
                if (t === null || t === undefined) return <Text type="secondary">-</Text>;
                return t === 0 ? <Tag color="green">未断网</Tag> : <Tag color="red">{BLOCK_TYPE[t]}</Tag>;
            },
        },
        { title: '初始流量额度', key: 'quota', width: 170, render: (_, r) => quotaText(r) },
        {
            title: '限速配置', key: 'limit', width: 260,
            render: (_, r) => (
                <Space size={6}>
                    {limitText(r)}
                    <EditOutlined onClick={() => openEditLimit(r)} style={{ color: '#1677ff', cursor: 'pointer' }} />
                </Space>
            ),
        },
        { title: '总消耗', dataIndex: 'used', key: 'used', width: 110 },
        { title: '流量余额', key: 'balance', width: 110, render: (_, r) => (r.quotaType === 'unlimited' ? '-' : r.balance) },
        { title: '导入时间', dataIndex: 'importTime', key: 'importTime', width: 170 },
        {
            title: '操作', key: 'action', fixed: 'right', width: 180,
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (_, r) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Space size={0}>
                        <Button type="link" size="small" onClick={() => openGift(r)}>赠送套餐</Button>
                        <Button type="link" size="small" onClick={() => setUsageCard(r)}>用量明细</Button>
                    </Space>
                    <Dropdown
                        menu={{
                            items: [
                                { key: 'limit', label: '限速变更记录' },
                                { key: 'gift', label: '流量增加记录' },
                            ],
                            onClick: ({ key }) => {
                                if (key === 'limit') setLimitChangeCard(r);
                                if (key === 'gift') openGiftRecord(r);
                            },
                        }}
                    >
                        <Button type="link" size="small">更多 <DownOutlined /></Button>
                    </Dropdown>
                </div>
            ),
        },
    ];

    const headerNoWrap = () => ({ style: { whiteSpace: 'nowrap' } });

    const dailyColumns = [
        { title: '日期', dataIndex: 'date', key: 'date' },
        { title: '当日消耗', dataIndex: 'used', key: 'used' },
        { title: '日终余额', dataIndex: 'balance', key: 'balance' },
    ];

    const limitChangeColumns = [
        { title: '变更时间', dataIndex: 'time', key: 'time', width: 160, onHeaderCell: headerNoWrap },
        {
            title: '操作人', key: 'operator', width: 130, onHeaderCell: headerNoWrap,
            render: (_, r) => (
                <div>
                    <div>{r.operatorName}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.operatorAccount}</Text>
                </div>
            ),
        },
        { title: '变更前', dataIndex: 'before', key: 'before', width: 200, onHeaderCell: headerNoWrap },
        { title: '变更后', dataIndex: 'after', key: 'after', width: 200, onHeaderCell: headerNoWrap },
        { title: '变更原因', dataIndex: 'reason', key: 'reason', onHeaderCell: headerNoWrap },
    ];

    const giftRecordColumns = [
        { title: '时间', dataIndex: 'time', key: 'time', width: 160, onHeaderCell: headerNoWrap },
        {
            title: '增加类型', key: 'type', width: 120, onHeaderCell: headerNoWrap,
            render: (_, r) => (r.type === 'initial' ? <Tag color="blue">初始赠送</Tag> : <Tag color="green">赠送套餐</Tag>),
        },
        {
            title: '操作人', key: 'operator', width: 130, onHeaderCell: headerNoWrap,
            render: (_, r) => (
                <div>
                    <div>{r.operatorName}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.operatorAccount}</Text>
                </div>
            ),
        },
        {
            title: '套餐', key: 'pkg', width: 220, onHeaderCell: headerNoWrap,
            render: (_, r) => (r.pkgName && r.pkgName !== '—'
                ? (<div><div>{r.pkgName}</div><Text type="secondary" style={{ fontSize: 12 }}>ID: {r.pkgId}</Text></div>)
                : <Text type="secondary">—</Text>),
        },
        { title: '流量额度', dataIndex: 'quota', key: 'quota', width: 150, onHeaderCell: headerNoWrap },
        { title: '原因', dataIndex: 'reason', key: 'reason', onHeaderCell: headerNoWrap },
    ];

    const initialGiftRecord = giftRecordCard
        ? { id: 'init', type: 'initial', time: giftRecordCard.importTime, operatorName: '系统', operatorAccount: '—', pkgName: '—', pkgId: '—', quota: quotaText(giftRecordCard), reason: '初始赠送' }
        : null;
    const giftRecordData = giftRecordCard
        ? [initialGiftRecord, ...(giftRecords[giftRecordCard.id] || [])].filter((r) => (!giftType || (r.type || 'gift') === giftType) && (!giftPkgName || (r.pkgName || '').includes(giftPkgName)) && (!giftPkgId || r.pkgId === giftPkgId))
        : [];

    // 卡片1：初始流量额度
    const renderQuotaCard = (quotaType) => (
        <Card size="small" title="初始流量额度" style={{ marginBottom: 16 }}>
            <Form.Item
                name="quotaType"
                rules={[{ required: true, message: '请选择初始流量额度' }]}
                style={{ marginBottom: quotaType === 'custom' ? 12 : 0 }}
                extra="无限流量＝运营商侧隐藏每月 100G、次月恢复（表象为无限流量）"
            >
                <Radio.Group options={QUOTA_TYPE_OPTIONS} />
            </Form.Item>
            {quotaType === 'custom' && (
                <Row gutter={16}>
                    <Col span={10}>
                        <Form.Item label="初始流量额度" name="quotaGb" rules={[{ required: true, message: '请输入初始流量额度' }]} style={{ marginBottom: 0 }}>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="GB" placeholder="请输入初始流量额度" />
                        </Form.Item>
                    </Col>
                    <Col span={14}>
                        <Form.Item
                            label="周期循环"
                            name="quotaCycle"
                            rules={[{ required: true, message: '请选择周期循环' }]}
                            style={{ marginBottom: 0 }}
                        >
                            <Radio.Group options={QUOTA_CYCLE_OPTIONS} />
                        </Form.Item>
                    </Col>
                </Row>
            )}
        </Card>
    );

    // 卡片2：限速配置
    const renderLimitCard = (limitAction) => (
        <Card size="small" title="限速配置">
            <Form.Item
                name="limitAction"
                rules={[{ required: true, message: '请选择限速配置' }]}
                style={{ marginBottom: limitAction === 'none' ? 0 : 12 }}
                extra="无限流量与定义额度均可配置；不配置＝不做限速处理；限速于次月恢复"
            >
                <Radio.Group options={LIMIT_ACTION_OPTIONS} />
            </Form.Item>
            {limitAction === 'speed' && (
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="达量阈值（每月达到）" name="limitThresholdGb" rules={[{ required: true, message: '请输入达量阈值' }]} style={{ marginBottom: 0 }}>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="GB" placeholder="每月达到多少 GB" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="限速档位" name="limitSpeedTier" rules={[{ required: true, message: '请选择限速档位' }]} style={{ marginBottom: 0 }}>
                            <Select placeholder="请选择限速档位" options={SPEED_TIER_OPTIONS} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
            )}
        </Card>
    );

    // 仅「未识别(无效)」卡可选；表头全选覆盖所有页的无效卡
    const invalidIds = dataSource.filter((d) => d.status === 0).map((d) => d.id);
    const selectedInvalidCount = selectedRowKeys.filter((id) => invalidIds.includes(id)).length;
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (r) => ({ disabled: r.status !== 0 }),
        columnTitle: (
            <Checkbox
                checked={invalidIds.length > 0 && selectedInvalidCount === invalidIds.length}
                indeterminate={selectedInvalidCount > 0 && selectedInvalidCount < invalidIds.length}
                onChange={(e) => setSelectedRowKeys(e.target.checked ? invalidIds : [])}
                disabled={invalidIds.length === 0}
            />
        ),
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>流量卡管理</Title>
                <Space size={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
                    <Segmented value={mockRole} onChange={setMockRole} options={ROLE_OPTIONS} />
                </Space>
            </div>

            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 8 }}>
                <Form layout="inline" form={searchForm}>
                    <Form.Item name="iccid" style={{ marginBottom: 0 }}>
                        <Input placeholder="ICCID" allowClear style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item name="msisdn" style={{ marginBottom: 0 }}>
                        <Input placeholder="MSISDN" allowClear style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="pool" style={{ marginBottom: 0 }}>
                        <Select
                            placeholder="流量池" allowClear showSearch optionFilterProp="label"
                            style={{ width: 180 }}
                            options={[
                                { label: '麦联宝', value: '麦联宝' },
                                { label: '万物互联电信', value: '万物互联电信' },
                                { label: '桔子智联', value: '桔子智联' },
                                { label: '五兴科技', value: '五兴科技' },
                                { label: '天贝云客猫', value: '天贝云客猫' },
                            ]}
                        />
                    </Form.Item>
                    {isPlatform && (
                        <>
                            <Form.Item name="entName" style={{ marginBottom: 0 }}>
                                <Input placeholder="企业名称（模糊）" allowClear style={{ width: 190 }} />
                            </Form.Item>
                            <Form.Item name="entId" style={{ marginBottom: 0 }}>
                                <Input placeholder="企业ID（精准）" allowClear style={{ width: 170 }} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="status" style={{ marginBottom: 0 }}>
                        <Select
                            placeholder="卡状态" allowClear style={{ width: 160 }}
                            options={Object.entries(CARD_STATUS).map(([value, v]) => ({ label: v.text, value: Number(value) }))}
                        />
                    </Form.Item>
                    <Form.Item name="netBlock" style={{ marginBottom: 0 }}>
                        <Select
                            placeholder="网络状态" allowClear style={{ width: 180 }}
                            options={Object.entries(BLOCK_TYPE).map(([value, text]) => ({ label: text, value: Number(value) }))}
                        />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={() => message.info('查询')}>查询</Button>
                            <Button icon={<ReloadOutlined />} onClick={() => searchForm.resetFields()}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ borderRadius: 8 }}>
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button type="primary" icon={<UploadOutlined />} onClick={openImport}>导入流量卡</Button>
                    <Button danger onClick={handleBatchDelete} disabled={selectedRowKeys.length === 0}>批量删除</Button>
                    <Button onClick={handleBatchIdentify} disabled={selectedRowKeys.length === 0}>批量识别卡号</Button>
                    {selectedRowKeys.length > 0 && <Text type="secondary" style={{ fontSize: 12 }}>已选 {selectedRowKeys.length} 项</Text>}
                    <Button icon={<ExportOutlined />} onClick={() => message.success('导出成功')}>导出列表</Button>
                </div>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={dataSource}
                    rowSelection={rowSelection}
                    scroll={{ x: 2420 }}
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                />
            </Card>

            <Modal
                title="导入流量卡"
                width={760}
                open={importOpen}
                onCancel={() => setImportOpen(false)}
                onOk={handleImport}
                okText="确定导入"
                destroyOnClose
            >
                <Form form={importForm} layout="vertical">
                    <Form.Item label="目标流量池" name="poolName" rules={[{ required: true, message: '请选择目标流量池' }]}>
                        <Select
                            placeholder="请选择流量池"
                            options={[
                                { label: '麦联宝', value: '麦联宝' },
                                { label: '万物互联电信', value: '万物互联电信' },
                                { label: '桔子智联', value: '桔子智联' },
                                { label: '五兴科技', value: '五兴科技' },
                                { label: '天贝云客猫', value: '天贝云客猫' },
                            ]}
                        />
                    </Form.Item>

                    {renderQuotaCard(importQuotaType)}
                    {renderLimitCard(importLimitAction)}

                    <Tabs
                        activeKey={importTab}
                        onChange={setImportTab}
                        items={[
                            {
                                key: 'manual',
                                label: '手动输入',
                                children: (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>ICCID、MSISDN 必填，ICCID 为主键且全局唯一；IMSI 选填。</Text>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>若某卡为「未识别(无效)」状态（导入时不在运营商卡池），支持再次导入以重新识别其最新状态。</Text>
                                        <div style={{ marginTop: 12 }}>
                                            <Form.List name="cards">
                                                {(fields, { add, remove }) => (
                                                    <>
                                                        {fields.map(({ key, name, ...restField }) => (
                                                            <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'iccid']}
                                                                    rules={[{ required: true, message: '请输入ICCID' }]}
                                                                    style={{ marginBottom: 0 }}
                                                                >
                                                                    <Input placeholder="ICCID" style={{ width: 200 }} />
                                                                </Form.Item>
                                                                <Form.Item {...restField} name={[name, 'msisdn']} rules={[{ required: true, message: '请输入MSISDN' }]} style={{ marginBottom: 0 }}>
                                                                    <Input placeholder="MSISDN" style={{ width: 150 }} />
                                                                </Form.Item>
                                                                <Form.Item {...restField} name={[name, 'imsi']} style={{ marginBottom: 0 }}>
                                                                    <Input placeholder="IMSI" style={{ width: 170 }} />
                                                                </Form.Item>
                                                                <DeleteOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                                            </Space>
                                                        ))}
                                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: '100%', marginTop: 8 }}>
                                                            添加一行
                                                        </Button>
                                                    </>
                                                )}
                                            </Form.List>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'file',
                                label: '文件导入',
                                children: (
                                    <div style={{ marginTop: 8 }}>
                                        <Upload beforeUpload={() => false} maxCount={1}>
                                            <Button icon={<UploadOutlined />} type="primary">上传文件</Button>
                                        </Upload>
                                        <div style={{ marginTop: 12 }}>
                                            <Link icon={<DownloadOutlined />} onClick={() => message.success('模板下载中')}>下载模板</Link>
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                                            模板列：ICCID（必填）、MSISDN（必填）、IMSI；流量额度与限速配置按上方统一设置；单次最大导入 5000 张。
                                        </Text>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Form>
            </Modal>

            <Modal
                title="修改限速配置"
                width={680}
                open={!!editLimit}
                onCancel={() => setEditLimit(null)}
                onOk={handleLimitSave}
                okText="保存"
                destroyOnClose
            >
                {editLimit && (
                    <Form form={limitForm} layout="vertical">
                        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="ICCID">{editLimit.iccid}</Descriptions.Item>
                            <Descriptions.Item label="所属流量池">{editLimit.poolName}</Descriptions.Item>
                            <Descriptions.Item label="初始流量额度">{quotaText(editLimit)}</Descriptions.Item>
                        </Descriptions>
                        {renderLimitCard(limitFormLimitAction)}
                        <Form.Item label="修改原因" name="reason" rules={[{ required: true, message: '请填写修改原因' }]}>
                            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请填写修改原因" />
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: 12 }}>保存后限速配置变更将下发运营商接口（CTIOT-5GCMP-CM013-自主限速）。</Text>
                    </Form>
                )}
            </Modal>

            <Modal
                title="赠送流量套餐"
                width={560}
                open={!!giftCard}
                onCancel={() => setGiftCard(null)}
                onOk={handleGiftSave}
                okText="赠送"
                destroyOnClose
            >
                {giftCard && (
                    <Form form={giftForm} layout="vertical">
                        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="ICCID">{giftCard.iccid}</Descriptions.Item>
                            <Descriptions.Item label="MSISDN">{giftCard.msisdn || '-'}</Descriptions.Item>
                            <Descriptions.Item label="所属流量池">{giftCard.poolName}</Descriptions.Item>
                        </Descriptions>
                        <Form.Item label="赠送套餐" name="pkgId" rules={[{ required: true, message: '请选择赠送套餐' }]} extra="可选任意套餐（含已下架）；赠送后为该卡增加套餐流量">
                            <Select
                                placeholder="请选择套餐"
                                options={PACKAGE_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item label="赠送原因" name="reason" rules={[{ required: true, message: '请填写赠送原因' }]}>
                            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请填写赠送原因" />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <Drawer
                title="限速变更记录"
                width={820}
                open={!!limitChangeCard}
                onClose={() => setLimitChangeCard(null)}
                destroyOnClose
            >
                {limitChangeCard && (
                    <>
                        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="ICCID">{limitChangeCard.iccid}</Descriptions.Item>
                            <Descriptions.Item label="流量池">{limitChangeCard.poolName}</Descriptions.Item>
                        </Descriptions>
                        <Table
                            rowKey="id"
                            size="small"
                            tableLayout="fixed"
                            columns={limitChangeColumns}
                            dataSource={limitChangeRecords[limitChangeCard.id] || []}
                            pagination={false}
                            locale={{ emptyText: '暂无限速变更记录' }}
                        />
                    </>
                )}
            </Drawer>

            <Drawer
                title="流量增加记录"
                width={900}
                open={!!giftRecordCard}
                onClose={() => setGiftRecordCard(null)}
                destroyOnClose
            >
                {giftRecordCard && (
                    <>
                        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="ICCID">{giftRecordCard.iccid}</Descriptions.Item>
                            <Descriptions.Item label="所属流量池">{giftRecordCard.poolName}</Descriptions.Item>
                        </Descriptions>
                        <Card bordered={false} style={{ marginBottom: 16, borderRadius: 8 }}>
                            <Form layout="inline" form={giftSearchForm}>
                                <Form.Item name="type" style={{ marginBottom: 0 }}>
                                    <Select
                                        placeholder="增加类型" allowClear style={{ width: 150 }}
                                        options={[{ label: '初始赠送', value: 'initial' }, { label: '赠送套餐', value: 'gift' }]}
                                    />
                                </Form.Item>
                                <Form.Item name="pkgName" style={{ marginBottom: 0 }}>
                                    <Input placeholder="套餐名称（模糊查询）" allowClear style={{ width: 200 }} />
                                </Form.Item>
                                <Form.Item name="pkgId" style={{ marginBottom: 0 }}>
                                    <Input placeholder="套餐ID（精准查询）" allowClear style={{ width: 200 }} />
                                </Form.Item>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Space>
                                        <Button type="primary" onClick={() => message.info('查询')}>查询</Button>
                                        <Button onClick={() => giftSearchForm.resetFields()}>重置</Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Card>
                        <div style={{ marginBottom: 16 }}>
                            <Button icon={<ExportOutlined />} onClick={() => message.success('导出成功')}>导出</Button>
                        </div>
                        <Table
                            rowKey="id"
                            size="small"
                            tableLayout="fixed"
                            columns={giftRecordColumns}
                            dataSource={giftRecordData}
                            pagination={false}
                            locale={{ emptyText: '暂无记录' }}
                        />
                    </>
                )}
            </Drawer>

            <Drawer
                title="用量明细"
                width={640}
                open={!!usageCard}
                onClose={() => setUsageCard(null)}
                destroyOnClose
            >
                {usageCard && (
                    <>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}><Card size="small"><Statistic title="总消耗" value={usageCard.used} /></Card></Col>
                            <Col span={8}><Card size="small"><Statistic title="流量余额" value={usageCard.balance} /></Card></Col>
                            <Col span={8}><Card size="small"><Statistic title="最后更新" value={usageCard.lastUpdate} valueStyle={{ fontSize: 16 }} /></Card></Col>
                        </Row>
                        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="ICCID">{usageCard.iccid}</Descriptions.Item>
                            <Descriptions.Item label="MSISDN">{usageCard.msisdn}</Descriptions.Item>
                            <Descriptions.Item label="所属流量池">{usageCard.poolName}</Descriptions.Item>
                        </Descriptions>
                        <div style={{ marginBottom: 12, fontWeight: 500 }}>每日用量</div>
                        <Table
                            rowKey="date"
                            size="small"
                            columns={dailyColumns}
                            dataSource={usageCard.daily}
                            pagination={false}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default CardManage;
